import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { defineMessages, FormattedMessage, injectIntl, useIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import Checkbox from '@/shared/components/input/Checkbox';
import api from '@/shared/api';
import enumCache from '@/shared/services/EnumCacheService';
import { dynamicSort, dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import classNames from 'classnames';
import DateInput from '@/shared/components/input/DateInput';
import config from '../../config';
import ModalTitleZendeskHelp from './ModalTitleZendeskHelp';

const messages = defineMessages({
  GenerateDocuments: 'Generate documents',
  TemplateType: 'Template type',
  Templates: 'Templates',
  NoTemplateForLanguage: 'No template found for this language',
  ChooseDocumentTemplatePerLanguage: 'ChooseDocumentTemplatePerLanguage',
  AddQrCode: 'Add QR code',
  ConvertToPdf: 'Convert to pdf?',
  FileType: 'File type',
  LinkDocumentTo: 'Link document to',
  MainTask: 'Main task',
  DocumentType: 'Document type',
  DocumentDate: 'Document date',
  DocumentStartDate: 'Document start date',
  DocumentEndDate: 'Document end date',
});
const defaultTemplateTypes = [3, 10, 13, 18, 21, 22, 23, 26, 27, 99];
const defaultDocumentTypes = [11, 12, 13, 29, 78, 93, 94, 144, 150, 5000];
//
const templateTypesWithDocumentTypeDropdown = [99];

class BatchGenerateDocumentModal extends Component {
  state = {
    asPdf: false,
    addQrCode: false,
    documentType: null,
    fileNameFormat: null,
    templates: {
      // template per language
    },
    subtaskTemplateId: null, // link to main task or subtask,
    documentDate: moment.utc().startOf('day'),
    documentStartDate: null,
    documentEndDate: null,
    templateType: null,
    templateTypes: [],

    languageTemplates: [],
    allDocumentTemplates: [],
    showValidation: false,
  };

  componentDidMount() {
    const { languages, templateTypes, intl, documentTypes } = this.props;
    if (!languages || languages.length === 0) return;
    Promise.all([
      enumCache.getTranslated('documenttype', intl),
      enumCache.getTranslated('templatetype', intl),
      api.base.get(`${config.appUrls.api}/api/documentTemplate/Templates`, {
        params: {
          templateTypes: templateTypes || defaultTemplateTypes,
        },
      }),
    ]).then(([documentTypesEnum, templateTypeEnum, { data: documentTemplates }]) => {
      const templateTypesInUse = new Set();
      const allDocumentTemplates = documentTemplates
        .map(template => ({
          key: template.id,
          label: template.name,
          templateType: template.templateType,
          templateTypeLabel: intl.formatMessage(listItemMsg('templatetype', template.templateType)),
          languageCode: template.languageCode,
        }))
        .sort(dynamicSortMultiple('templateTypeLabel', 'label'));

      const languageTemplates = languages.map(langCode => {
        const templatesForLang = allDocumentTemplates.filter(x => x.languageCode === langCode);
        return {
          key: langCode,
          label: intl.formatMessage(listItemMsg('language', langCode, langCode)),
          templateTypes: Object.values(
            // Group by templateType
            templatesForLang.reduce((obj, template) => {
              if (!obj[template.templateType])
                // eslint-disable-next-line no-param-reassign
                obj[template.templateType] = {
                  key: template.templateType,
                  label: template.templateTypeLabel,
                  templates: [],
                };
              obj[template.templateType].templates.push(template);
              templateTypesInUse.add(template.templateType);
              return obj;
            }, {}),
          ).sort(dynamicSort('label')),
        };
      });

      this.setState({
        languageTemplates,
        templates: {},
        documentTypes: documentTypesEnum.filter(x => (documentTypes || []).indexOf(x.id) >= 0),
        templateTypes: templateTypeEnum.filter(x => templateTypesInUse.has(x.id)),
        allDocumentTemplates,
      });
    });
  }

  templateChanged = (langCode, templateId) => {
    this.setState(
      ({ templates, allDocumentTemplates, templateType: oldTemplateType, documentType }) => {
        let templateType = null;
        if (templateId) {
          ({ templateType } = allDocumentTemplates.find(x => x.key === templateId) || {});
        }

        return {
          templates: { ...templates, [langCode]: templateId || null },
          templateType,
          documentType: oldTemplateType !== templateType ? null : documentType,
        };
      },
    );
  };
  templateTypeChanged = e => {
    const templateType = e.target.value || null;

    this.setState({
      templateType: templateType ? parseInt(templateType) : null,
      templates: {},
      documentType: null,
    });
  };

  asPdfChanged = e => {
    const value = parseInt(e.target.value);
    this.setState({ asPdf: value === 1 });
  };

  isValid = () => {
    const { templates, documentDate, documentStartDate, documentEndDate } = this.state;
    if (!Object.keys(templates).some(langCode => !!templates[langCode])) return false;
    if (!documentDate || !moment(documentDate).isValid()) return false;
    if (documentStartDate && !moment(documentStartDate).isValid()) return false;
    if (documentEndDate && !moment(documentEndDate).isValid()) return false;
    if (documentStartDate && documentEndDate && documentEndDate.isBefore(documentStartDate, 'day'))
      return false;
    return true;
  };

  onSave = () => {
    if (!this.isValid()) return;
    const {
      templates,
      asPdf,
      addQrCode,
      documentType,
      fileNameFormat,
      subtaskTemplateId,
      documentDate,
      documentStartDate,
      documentEndDate,
    } = this.state;
    const { onSave } = this.props;
    const languagesWithTemplateId = Object.keys(templates).reduce((result, langCode) => {
      if (!templates[langCode]) return result;
      // eslint-disable-next-line no-param-reassign
      result[langCode] = templates[langCode];
      return result;
    }, {});

    const parameters = {
      templates: languagesWithTemplateId,
      asPdf,
      addQrCode,
      documentType,
      fileNameFormat,
      subtaskTemplateId,
      documentDate,
      documentStartDate,
      documentEndDate,
    };

    return onSave(parameters);
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  render() {
    const { show, intl, subtasks, languages } = this.props;
    const {
      languageTemplates,
      templates,
      asPdf,
      addQrCode,
      documentType,
      fileNameFormat,
      subtaskTemplateId,
      showValidation,
      documentTypes,
      documentDate,
      documentStartDate,
      documentEndDate,
      templateType,
      templateTypes,
    } = this.state;

    const showTemplateTypeSelect = languages && languages.length > 1;
    const showTemplates = !showTemplateTypeSelect || !!templateType;

    const showDocumentTypeSelect =
      !!templateType && templateTypesWithDocumentTypeDropdown.indexOf(templateType) >= 0;

    return (
      <OkCancelModal
        title={
          <ModalTitleZendeskHelp message={messages.GenerateDocuments} article="5942250739730" />
        }
        show={show}
        onClose={this.closeModal}
        onSave={this.onSave}
        disabled={showValidation && !this.isValid()}
        onHoverSave={() => this.setState({ showValidation: true })}
        size="lg"
      >
        {show && languageTemplates.length > 0 && (
          <form>
            <div className="form-horizontal">
              {showTemplateTypeSelect ? (
                <div
                  className={classNames('form-group', {
                    'has-error': showValidation && templateType === null,
                  })}
                >
                  <label className="control-label col-sm-3">
                    <FormattedMessage {...messages.TemplateType} />
                  </label>
                  <div className="col-md-6">
                    <select
                      onChange={this.templateTypeChanged}
                      className="form-control"
                      value={templateType || ''}
                    >
                      {!templateType && <option value="" />}
                      {templateTypes.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : null}
              <p className="col-sm-9 col-sm-offset-3">
                <FormattedMessage {...messages.ChooseDocumentTemplatePerLanguage} />
              </p>
              {languageTemplates.map(language => {
                const selectableTemplates =
                  templateType !== null && showTemplateTypeSelect
                    ? (language.templateTypes || []).filter(x => x.key === templateType)
                    : language.templateTypes || [];

                return (
                  <div
                    key={language.key}
                    className={classNames('form-group', {
                      'has-error':
                        showValidation &&
                        selectableTemplates.length > 0 &&
                        !templates[language.key],
                    })}
                  >
                    <label className="control-label col-sm-3">{language.label}</label>
                    <div className="col-md-6">
                      {selectableTemplates.length > 0 ? (
                        <select
                          onChange={e => this.templateChanged(language.key, e.target.value)}
                          className="form-control"
                          value={templates[language.key] || ''}
                          disabled={!showTemplates}
                        >
                          <option value="" />
                          {selectableTemplates.map(group => (
                            <optgroup key={group.key} label={group.label}>
                              {group.templates.map(template => (
                                <option key={template.key} value={template.key}>
                                  {template.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      ) : (
                        <div className="form-control-static text-danger">
                          <FormattedMessage {...messages.NoTemplateForLanguage} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="form-group">
                <label className="control-label col-sm-3">
                  <FormattedMessage {...messages.DocumentDate} />
                </label>
                <div className="col-md-6">
                  <DateInput
                    value={documentDate}
                    onChange={date => this.setState({ documentDate: date })}
                    placeholder={intl.formatMessage(messages.DocumentDate)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="control-label col-sm-3">
                  <FormattedMessage {...messages.DocumentStartDate} />
                </label>
                <div className="col-md-6">
                  <DateInput
                    value={documentStartDate}
                    onChange={date => this.setState({ documentStartDate: date })}
                    placeholder={intl.formatMessage(messages.DocumentStartDate)}
                    required={false}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="control-label col-sm-3">
                  <FormattedMessage {...messages.DocumentEndDate} />
                </label>
                <div className="col-md-6">
                  <DateInput
                    value={documentEndDate}
                    onChange={date => this.setState({ documentEndDate: date })}
                    placeholder={intl.formatMessage(messages.DocumentEndDate)}
                    required={false}
                  />
                </div>
              </div>
              {subtasks && subtasks.length > 0 ? (
                <div className="form-group">
                  <label className="control-label col-sm-3">
                    <FormattedMessage {...messages.LinkDocumentTo} />
                  </label>
                  <div className="col-md-6">
                    <select
                      onChange={e => this.setState({ subtaskTemplateId: e.target.value || null })}
                      className="form-control"
                      value={subtaskTemplateId || ''}
                    >
                      <option value="">{intl.formatMessage(messages.MainTask)}</option>
                      {subtasks.map(t => (
                        <option key={t.id} value={t.id}>
                          {intl.formatMessage(listItemMsg('tasktemplate', t.id, t.name))}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : null}
              {showDocumentTypeSelect && documentTypes && documentTypes.length > 0 ? (
                <div className="form-group">
                  <label className="control-label col-sm-3">
                    <FormattedMessage {...messages.DocumentType} />
                  </label>
                  <div className="col-md-6">
                    <select
                      onChange={e => this.setState({ documentType: e.target.value || null })}
                      className="form-control"
                      value={documentType || ''}
                    >
                      <option value="" />
                      {documentTypes.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : null}
              <div className="form-group">
                <div className="col-md-9 col-md-offset-3 margin5T">
                  <Checkbox
                    id="asPdf"
                    value={!!asPdf}
                    onClick={value => this.setState({ asPdf: value })}
                    text={intl.formatMessage(messages.ConvertToPdf)}
                  />
                </div>
              </div>
              <div className="form-group">
                <div className="col-md-9 col-md-offset-3 margin5T">
                  <Checkbox
                    id="addQrCode"
                    value={addQrCode}
                    onClick={value => this.setState({ addQrCode: value })}
                    text={intl.formatMessage(messages.AddQrCode)}
                  />
                </div>
              </div>
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}

BatchGenerateDocumentModal.propTypes = {
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  templateTypes: PropTypes.arrayOf(PropTypes.number),
  documentTypes: PropTypes.arrayOf(PropTypes.number),
  show: PropTypes.bool,
  subtasks: PropTypes.arrayOf(PropTypes.shape({})),
  intl: intlShape.isRequired,
};
BatchGenerateDocumentModal.defaultProps = {
  templateTypes: defaultTemplateTypes,
  documentTypes: defaultDocumentTypes,
  show: true,
  subtasks: [],
};

export default injectIntl(BatchGenerateDocumentModal);
